import { acceptHMRUpdate, defineStore } from 'pinia'
import { createVNode, render, toRaw } from 'vue'
import { useMaps } from './maps'
import { useMapSvgOverlaysStore } from './mapSvgOverlays'
import parkingAreaIcon from '@/assets/icons/parking-sign.svg'
import parkingPositionIcon from '@/assets/icons/parking-position.svg'
import app from '@/main'
import ParkingLotInfoWindow from '@/components/map/ParkingLotInfoWindow.vue'
import { postExtractGeometryFromShape } from '../scripts/fieldsPlanning'
import { useRoiStore } from './roi'
import { useFieldsConnectionStore } from './fieldsConnection'
import {
    DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
    DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
    PATH_CENTER_LINE_MODE_CENTER,
    normalizeCenterLineOptions,
} from '../helpers/parkingLotPathGenerator'
import { useNewPlannedParkingLotStore } from './newPlannedParkingLot'
import { v4 as uuid } from 'uuid'
import {
    getParkingLots,
    getPlannedParkingSlots,
    deleteParkingLot,
    deletePlannedParkingSlot as deletePlannedParkingSlotApi
} from '../scripts/task'
import { isRectangle } from '@/helpers/geometryHelper'

const PARKING_LOT_BORDER_COLOR = '#5EA529'
const PARKING_LOT_BORDER_STROKE_WEIGHT = 3
const PARKING_LOT_FILL_COLOR = '#9AE630'
const PARKING_LOT_FILL_OPACITY = 0.1
const PARKING_LOT_SELECTED_STROKE_WEIGHT = 10
const CENTER_PATH_STROKE_COLOR = '#F59E0B'
const PATH_STROKE_COLOR = '#27B5FF'
const PATH_STROKE_WEIGHT = 3
const PARKING_LOT_Z_INDEX = 50
const PARKING_SIGN_SIZE_PX = 40
const PARKING_LOTS_OVERLAY_GROUP = 'parkingLots'
/** Local border id for preview/map until save (not sent to API). */
const clientBorderId = (parkingLotId) => `${parkingLotId}-border`
const plannedBorderId = (slotId) => `planned-border-${slotId}`
const plannedPathId = (slotId) => `planned-path-${slotId}`

/** Keys for planned parking slot path parameters. */
const PLANNED_PATH_PARAM_KEYS = [
    'pathLineSpacingMeters',
    'pathCenterLineMode',
    'pathCenterLineOffsetSide',
    'pathCenterLineOffsetFromSideMeters',
]

export const useParkingLotsStore = defineStore('parkingLots', {
    state: () => ({
        parkingLots: [],
        existingParkingLots: [],
        /** New parking lots (draft polygons), Field Connection only */
        newParkingLots: [],
        selectedNewParkingLotId: null,
        /** Editable border overlay while replacing an existing lot's border (key: parking lot id). */
        borderEdits: {},
        parkingInfoWindow: null,
        parkingInfoWindowEl: null,
        isLoading: false,
        errorMessage: null,
        creatingParkingLot: false,
        selectedParkingLotId: null,
        mapClickListener: null,
        editingParkingLotId: null,
        /** Snapshot when edit panel opens; restored on discard. */
        editingParkingLotBackup: null,
        newParkingLotBackup: null,
        parkingLotForm: null,
        parkingLotFormSnapshot: null,
        /** Existing planned slot: keep saved path until user edits path fields. */
        skipPlannedPathRegen: false,
        /** Restored after map measuring ends. */
        _wasListeningBeforeMeasure: false,
        _parkingMapInteractionPausedForMeasure: false,
        _newLotIdsOnMapForMeasure: [],
    }),
    actions: {
        /** Planned parking area (vs legacy parking position). Set locally on load/create. */
        isPlannedParkingSlot(lot) {
            return lot?.isPlannedSlot === true
        },
        /** Gets first border for lot (planned: synthetic from border field, legacy: first from borders array). */
        getBorder(lot) {
            if (this.isPlannedParkingSlot(lot)) {
                const geometry = lot?.border
                if (!geometry?.coordinates || geometry.type !== 'Polygon') return null
                return { id: plannedBorderId(lot.id), geometry }
            }
            return (Array.isArray(lot?.borders) ? lot.borders : [])[0] || null
        },
        getPathGeometry(lot) {
            if (this.isPlannedParkingSlot(lot)) {
                return lot?.path?.coordinates ? lot.path : null
            }
            const paths = Array.isArray(lot?.paths) ? lot.paths : []
            return paths[0]?.geometry ?? null
        },
        getPathPreviewGeometry(lot) {
            if (this.isPlannedParkingSlot(lot)) return this.getPathGeometry(lot)

            const lines = (lot?.paths || []).flatMap(p => 
                p?.geometry?.type === 'MultiLineString' ? p.geometry.coordinates : []
            )

            return lines.length ? { type: 'MultiLineString', coordinates: lines } : null
        },
        getBorderId(lot) {
            return this.getBorder(lot)?.id ?? null
        },
        isClientBorderId(borderId, parkingLotId) {
            return borderId != null && parkingLotId != null && borderId === clientBorderId(parkingLotId)
        },
        hasBorder(lot) {
            const g = this.getLotPolygonGeometry(lot)
            return g?.type === 'Polygon' && !!g?.coordinates
        },
        getLotPolygonGeometry(lot) {
            if (!lot) return null
            if (this.isPlannedParkingSlot(lot)) {
                if (lot?.border?.type === 'Polygon') return lot.border
                if (lot?.feature?.geometry?.type === 'Polygon') return lot.feature.geometry
                return null
            }
            const borderGeometry = this.getBorder(lot)?.geometry
            if (borderGeometry?.coordinates && borderGeometry.type === 'Polygon') {
                return borderGeometry
            }
            if (lot?.feature?.geometry?.type === 'Polygon') {
                return lot.feature.geometry
            }
            return null
        },
        getPathIds(lot) {
            if (this.isPlannedParkingSlot(lot)) {
                return this.getPathGeometry(lot) ? [plannedPathId(lot.id)] : []
            }
            return this.getParkingLotPathIds(lot)
        },
        sortExistingParkingLotsForDisplay(lots) {
            return [...lots].sort((a, b) => {
                const aHas = this.hasBorder(a)
                const bHas = this.hasBorder(b)
                if (aHas === bHas) return 0
                return aHas ? 1 : -1
            })
        },
        initExistingParkingLot(parkingLot, isPlannedSlot = false) {
            return {
                ...parkingLot,
                isPlannedSlot,
                pathLineSpacingMeters: parkingLot?.pathLineSpacingMeters
                    ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                pathCenterLineMode: parkingLot?.pathCenterLineMode ?? PATH_CENTER_LINE_MODE_CENTER,
                pathCenterLineOffsetSide: parkingLot?.pathCenterLineOffsetSide ?? 'a',
                pathCenterLineOffsetFromSideMeters: parkingLot?.pathCenterLineOffsetFromSideMeters
                    ?? DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
                pathPolylines: parkingLot?.pathPolylines ?? [],
                isModified: parkingLot?.isModified ?? false,
            }
        },
        defaultParkingLotForm() {
            return {
                name: '',
                active: null,
                border: {
                    valid_from: null,
                    valid_to: null,
                },
                paths: [],
            }
        },
        defaultNewParkingLotForm() {
            return {
                name: '',
                active: null,
                pathLineSpacingMeters: DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                pathCenterLineMode: PATH_CENTER_LINE_MODE_CENTER,
                pathCenterLineOffsetSide: 'a',
                pathCenterLineOffsetFromSideMeters: DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
            }
        },
        parkingLotFormPathFromRaw(path) {
            return {
                id: path?.id ?? null,
                valid_from: path?.valid_from ? new Date(path.valid_from) : null,
                valid_to: path?.valid_to ? new Date(path.valid_to) : null,
            }
        },
        parkingLotFormFromLot(lot) {
            if (this.isPlannedParkingSlot(lot)) {
                if (this.editingParkingLotId === lot.id) {
                    const defaults = this.defaultNewParkingLotForm()
                    return {
                        name: lot?.name ?? '',
                        active: lot?.active ?? null,
                        pathLineSpacingMeters: defaults.pathLineSpacingMeters,
                        pathCenterLineMode: defaults.pathCenterLineMode,
                        pathCenterLineOffsetSide: defaults.pathCenterLineOffsetSide,
                        pathCenterLineOffsetFromSideMeters: defaults.pathCenterLineOffsetFromSideMeters,
                    }
                }
                return this.parkingLotFormFromNewLot(lot)
            }

            const border = this.getBorder(lot)
            const paths = Array.isArray(lot?.paths) ? lot.paths : []
            return {
                name: lot?.name ?? '',
                active: lot?.active ?? null,
                border: {
                    valid_from: border?.valid_from ? new Date(border.valid_from) : null,
                    valid_to: border?.valid_to ? new Date(border.valid_to) : null,
                },
                paths: paths.map(path => this.parkingLotFormPathFromRaw(path)),
            }
        },
        formDateToCompareValue(value) {
            if (value == null) return null
            const time = (value instanceof Date ? value : new Date(value)).getTime()
            return Number.isNaN(time) ? null : time
        },
        normalizeFormForCompare(form) {
            if (!form) return null

            return {
                name: form.name ?? '',
                active: form.active ?? null,
                border: {
                    valid_from: this.formDateToCompareValue(form.border?.valid_from),
                    valid_to: this.formDateToCompareValue(form.border?.valid_to),
                },
                paths: (Array.isArray(form.paths) ? form.paths : []).map(path => ({
                    id: path.id ?? null,
                    valid_from: this.formDateToCompareValue(path.valid_from),
                    valid_to: this.formDateToCompareValue(path.valid_to),
                })),
            }
        },
        parkingLotFormFromNewLot(lot) {
            return {
                name: lot?.name ?? '',
                active: lot?.active ?? null,
                pathLineSpacingMeters: lot?.pathLineSpacingMeters
                    ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                pathCenterLineMode: lot?.pathCenterLineMode ?? PATH_CENTER_LINE_MODE_CENTER,
                pathCenterLineOffsetSide: lot?.pathCenterLineOffsetSide ?? 'a',
                pathCenterLineOffsetFromSideMeters: lot?.pathCenterLineOffsetFromSideMeters
                    ?? DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
            }
        },
        normalizeNewLotFormForCompare(form) {
            if (!form) return null
            return {
                name: form.name ?? '',
                active: form.active ?? null,
                pathLineSpacingMeters: form.pathLineSpacingMeters
                    ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                pathCenterLineMode: form.pathCenterLineMode ?? PATH_CENTER_LINE_MODE_CENTER,
                pathCenterLineOffsetSide: form.pathCenterLineOffsetSide ?? 'a',
                pathCenterLineOffsetFromSideMeters: form.pathCenterLineOffsetFromSideMeters
                    ?? DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
            }
        },
        applyPlannedPathFormFieldsToLot(lot, form) {
            lot.name = form.name
            lot.active = form.active
            lot.pathLineSpacingMeters = form.pathLineSpacingMeters
                ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS
            const centerLine = this.centerLineOptionsFromForm(form)
            lot.pathCenterLineMode = centerLine.position
            lot.pathCenterLineOffsetSide = centerLine.offsetSide
            lot.pathCenterLineOffsetFromSideMeters = centerLine.offsetFromSideMeters
        },
        getPlannedPathEditLot() {
            if (this.selectedNewParkingLotId != null) {
                return this.getNewParkingLotById(this.selectedNewParkingLotId)
            }
            if (this.editingParkingLotId == null) return null
            const lot = this.existingParkingLots.find(p => p.id === this.editingParkingLotId)
            return this.isPlannedParkingSlot(lot) ? lot : null
        },
        centerLineOptionsFromForm(form) {
            if (!form) return normalizeCenterLineOptions({})
            return normalizeCenterLineOptions({
                position: form.pathCenterLineMode,
                offsetSide: form.pathCenterLineOffsetSide,
                offsetFromSideMeters: form.pathCenterLineOffsetFromSideMeters,
            })
        },
        getCenterLineOptionsForLot(lot) {
            const form = this.parkingLotForm
            const editing = this.getPlannedPathEditLot()
            if (form && editing?.id === lot?.id && !this.skipPlannedPathRegen) {
                return this.centerLineOptionsFromForm(form)
            }
            return normalizeCenterLineOptions({
                position: lot?.pathCenterLineMode,
                offsetSide: lot?.pathCenterLineOffsetSide,
                offsetFromSideMeters: lot?.pathCenterLineOffsetFromSideMeters,
            })
        },
        applyPlannedPathFormToLot(lot, form = this.parkingLotForm) {
            if (!form || !lot) return

            const centerLine = this.centerLineOptionsFromForm(form)
            lot.pathLineSpacingMeters = form.pathLineSpacingMeters
                ?? DEFAULT_PARKING_PATH_LINE_SPACING_METERS
            lot.pathCenterLineMode = centerLine.position
            lot.pathCenterLineOffsetSide = centerLine.offsetSide
            lot.pathCenterLineOffsetFromSideMeters = centerLine.offsetFromSideMeters
        },
        previewPlannedPathFromForm() {
            if (this.skipPlannedPathRegen) return

            const lot = this.getPlannedPathEditLot()
            const newPark = useNewPlannedParkingLotStore()
            if (!lot) return

            this.applyPlannedPathFormToLot(lot)
            newPark.applyPathToLot(lot)
        },
        editablePolygonStyle() {
            return {
                fillColor: PARKING_LOT_FILL_COLOR,
                fillOpacity: 0.5,
                strokeColor: PARKING_LOT_BORDER_COLOR,
                strokeWeight: PARKING_LOT_BORDER_STROKE_WEIGHT,
                zIndex: 1000,
            }
        },
        createParkingPolygonDraft(feature) {
            const roi = useRoiStore()
            return roi.createEditablePolygonDraft(feature, this.editablePolygonStyle())
        },
        attachParkingPolygonListeners(draft, onCoordinatesChanged) {
            const roi = useRoiStore()
            roi.attachPolygonDraftListeners(draft, onCoordinatesChanged)
        },
        disposeParkingPolygonDraft(draft) {
            const roi = useRoiStore()
            roi.disposePolygonDraft(draft)
        },
        getNewParkingLotById(id) {
            return this.newParkingLots.find(lot => lot.id === id) ?? null
        },
        addNewParkingLotFromFeature(feature, terraDrawFeatureId = null) {
            if (feature?.geometry?.type !== 'Polygon') return null

            const newPark = useNewPlannedParkingLotStore()
            const newLot = {
                id: uuid(),
                isPlannedSlot: true,
                feature: JSON.parse(JSON.stringify(feature)),
                terraDrawFeatureId: terraDrawFeatureId ?? null,
                name: '',
                active: true,
                path: null,
                pathPolylines: [],
                pathLineSpacingMeters: DEFAULT_PARKING_PATH_LINE_SPACING_METERS,
                pathCenterLineMode: PATH_CENTER_LINE_MODE_CENTER,
                pathCenterLineOffsetSide: 'a',
                pathCenterLineOffsetFromSideMeters: DEFAULT_PATH_CENTER_LINE_OFFSET_FROM_SIDE_METERS,
                generator_version: 1,
                survey_id: null,
            }

            if (terraDrawFeatureId) {
                newLot.terraDrawFeatureId = terraDrawFeatureId
            } else {
                newPark.addLotToTerraDraw(newLot)
            }
            newPark.ensureChangeListener()
            newPark.applyPathToLot(newLot)

            this.newParkingLots.push(newLot)
            return newLot
        },
        clearNewParkingLotPathPolylines(lot) {
            const newPark = useNewPlannedParkingLotStore()
            newPark.clearLotPathPolylines(lot)
        },
        syncNewParkingLotGeometryFromDraft(draft) {
            const geometry = draft?.feature?.geometry
            if (!geometry) return

            const lot = this.getNewParkingLotById(draft.id)
            if (!lot) return

            lot.feature = {
                ...lot.feature,
                geometry: JSON.parse(JSON.stringify(geometry)),
            }
        },
        centerMapOnParkingLot(lot) {
            const ring = this.getLotPolygonGeometry(lot)?.coordinates?.[0]
            const maps = useMaps()
            if (!ring?.length || !maps.map || !maps.google) return

            const bounds = new maps.google.maps.LatLngBounds()
            ring.forEach(([lng, lat]) => bounds.extend(new maps.google.maps.LatLng(lat, lng)))
            maps.map.fitBounds(bounds, { padding: 50 })
        },
        cloneParkingLotState(lot) {
            if (!lot) return null

            return JSON.parse(JSON.stringify({
                id: lot.id,
                isPlannedSlot: lot.isPlannedSlot === true,
                name: lot.name,
                active: lot.active,
                isModified: lot.isModified,
                border: lot.border ?? null,
                feature: lot.feature ?? null,
                path: this.getPathGeometry(lot) ?? null,
                paths: lot.paths ?? [],
                borders: lot.borders ?? [],
                pathLineSpacingMeters: lot.pathLineSpacingMeters,
                pathCenterLineMode: lot.pathCenterLineMode,
                pathCenterLineOffsetSide: lot.pathCenterLineOffsetSide,
                pathCenterLineOffsetFromSideMeters: lot.pathCenterLineOffsetFromSideMeters,
                generator_version: lot.generator_version,
                survey_id: lot.survey_id,
            }))
        },
        lotDiffersFromBackup(lot, backup) {
            if (!lot || !backup || backup.id !== lot.id) return false

            const isPlanned = this.isPlannedParkingSlot(lot)
            if (isPlanned) {
                if (JSON.stringify(lot.border ?? null) !== JSON.stringify(backup.border ?? null)) {
                    return true
                }
            } else if (JSON.stringify(lot.borders ?? []) !== JSON.stringify(backup.borders ?? [])) {
                return true
            }

            if (JSON.stringify(this.getPathGeometry(lot)) !== JSON.stringify(backup.path ?? null)) {
                return true
            }
            if (JSON.stringify(lot.paths ?? []) !== JSON.stringify(backup.paths ?? [])) {
                return true
            }
            if (JSON.stringify(lot.feature ?? null) !== JSON.stringify(backup.feature ?? null)) {
                return true
            }

            return PLANNED_PATH_PARAM_KEYS.some(key => lot[key] !== backup[key])
        },
        applyBackupFromSnapshot(lot, backup, { existing = false } = {}) {
            if (!lot || !backup || backup.id !== lot.id) return

            const newPark = useNewPlannedParkingLotStore()
            const copy = value => (value != null ? JSON.parse(JSON.stringify(value)) : null)

            lot.name = backup.name
            lot.active = backup.active
            lot.feature = copy(backup.feature)
            lot.path = copy(backup.path)
            PLANNED_PATH_PARAM_KEYS.forEach(key => {
                lot[key] = backup[key]
            })
            lot.generator_version = backup.generator_version
            lot.survey_id = backup.survey_id
            newPark.clearLotPathPolylines(lot)

            if (existing) {
                lot.isModified = backup.isModified
                lot.border = copy(backup.border)
                lot.paths = copy(backup.paths) ?? []
                lot.borders = copy(backup.borders) ?? []
                lot.terraDrawFeatureId = null
            }
        },
        clearLotMapVisuals(lot) {
            if (!lot) return
            this.removeParkingLotMapOverlays({
                parkingLotId: lot.id,
                borderId: this.getBorderId(lot),
                pathIds: this.getPathIds(lot),
            })
        },
        resetExistingEditSession() {
            this.skipPlannedPathRegen = false
            this.clearParkingLotHighlight()
            this.editingParkingLotId = null
            this.editingParkingLotBackup = null
            this.parkingLotForm = null
            this.parkingLotFormSnapshot = null
        },
        resetNewLotEditSession() {
            this.selectedNewParkingLotId = null
            this.newParkingLotBackup = null
            this.parkingLotForm = null
            this.parkingLotFormSnapshot = null
        },
        discardParkingLotEdit(lot) {
            if (!lot) return

            const newPark = useNewPlannedParkingLotStore()
            newPark.removePathFromLot(lot)
            if (lot.terraDrawFeatureId) {
                newPark.removeLotFromTerraDraw(lot)
            }
            this.clearLotMapVisuals(lot)
            this.applyBackupFromSnapshot(lot, this.editingParkingLotBackup, { existing: true })
            this.clearBorderEdit(lot.id)

            if (this.isPlannedParkingSlot(lot)) {
                newPark.finalizePlannedLotEdit(lot, { syncFromTerra: false })
            } else {
                this.drawParkingLotOnMap(lot)
            }
        },
        discardNewParkingLotEdit(lot) {
            if (!lot) return

            const newPark = useNewPlannedParkingLotStore()
            newPark.removePathFromLot(lot)
            if (lot.terraDrawFeatureId) {
                newPark.removeLotFromTerraDraw(lot)
            }
            this.applyBackupFromSnapshot(lot, this.newParkingLotBackup)
            if (this.hasBorder(lot)) {
                newPark.addLotToTerraDraw(lot)
                newPark.applyPathToLot(lot)
            }
        },
        selectNewParkingLot(id) {
            const newPark = useNewPlannedParkingLotStore()

            if (this.editingParkingLotId != null) {
                const existing = this.existingParkingLots.find(
                    p => p.id === this.editingParkingLotId,
                )
                if (existing) this.discardParkingLotEdit(existing)
                this.resetExistingEditSession()
            }

            if (
                this.selectedNewParkingLotId != null
                && this.selectedNewParkingLotId !== id
            ) {
                this.commitNewParkingLotEdit(this.selectedNewParkingLotId)
            }

            newPark.endAllExistingPlannedTerraEdits()
            this.selectedNewParkingLotId = id
            const lot = this.getNewParkingLotById(id)
            this.newParkingLotBackup = this.cloneParkingLotState(lot)
            this.loadParkingLotEditForm()
            this.ensureParkingLotMapClickListener()
            if (lot?.feature?.geometry?.coordinates) {
                this.centerMapOnParkingLot(lot)
            }
            newPark.beginNewLotEdit(lot)
        },
        commitNewParkingLotEdit(lotId = this.selectedNewParkingLotId) {
            const lot = lotId != null ? this.getNewParkingLotById(lotId) : null
            if (!lot) return false

            const newPark = useNewPlannedParkingLotStore()
            if (lot.terraDrawFeatureId) {
                newPark.syncLotBorderFromTerraDraw(lot)
            }

            if (this.parkingLotForm) {
                lot.name = this.parkingLotForm.name ?? ''
                lot.active = this.parkingLotForm.active ?? null
                if (!lot.terraDrawFeatureId) {
                    this.applyPlannedPathFormFieldsToLot(lot, this.parkingLotForm)
                    newPark.applyPathToLot(lot)
                }
            }

            if (this.selectedNewParkingLotId === lotId) {
                this.newParkingLotBackup = this.cloneParkingLotState(lot)
                if (this.parkingLotForm) {
                    this.parkingLotFormSnapshot = this.normalizeNewLotFormForCompare(
                        this.parkingLotForm,
                    )
                }
            }

            return true
        },
        closeNewParkingLotEdit({ discard = false } = {}) {
            const lot = this.selectedNewParkingLotId != null
                ? this.getNewParkingLotById(this.selectedNewParkingLotId)
                : null

            if (lot && discard) {
                this.discardNewParkingLotEdit(lot)
            }

            this.resetNewLotEditSession()
        },
        removeNewParkingLot(id) {
            const lot = this.getNewParkingLotById(id)
            const newPark = useNewPlannedParkingLotStore()
            if (lot) {
                this.clearNewParkingLotPathPolylines(lot)
                newPark.removeLotFromTerraDraw(lot)
            }
            this.newParkingLots = this.newParkingLots.filter(lot => lot.id !== id)
            if (this.selectedNewParkingLotId === id) {
                this.closeNewParkingLotEdit()
            }
        },
        clearNewParkingLots() {
            const newPark = useNewPlannedParkingLotStore()
            this.newParkingLots.forEach(lot => {
                this.clearNewParkingLotPathPolylines(lot)
                newPark.removeLotFromTerraDraw(lot)
            })
            this.newParkingLots = []
            this.closeNewParkingLotEdit()
        },
        clearBorderEdit(parkingLotId) {
            const draft = this.borderEdits[parkingLotId]
            if (!draft) return
            this.disposeParkingPolygonDraft(draft)
            delete this.borderEdits[parkingLotId]
        },
        clearAllBorderEdits() {
            Object.keys(this.borderEdits).forEach(id => this.clearBorderEdit(Number(id)))
        },
        syncExistingLotBorderFromDraft(parkingLotId, draft) {
            const geometry = draft?.feature?.geometry
            if (parkingLotId == null || !geometry) return

            const lot = this.existingParkingLots.find(p => p.id === parkingLotId)
            const border = this.getBorder(lot)
            if (!border) return

            border.geometry = JSON.parse(JSON.stringify(geometry))
            lot.isModified = true
        },
        applyFormToLot(lot, form) {
            if (!lot || !form) return

            if (this.isPlannedParkingSlot(lot)) {
                lot.name = form.name
                lot.active = form.active
                if (!this.skipPlannedPathRegen) {
                    this.applyPlannedPathFormFieldsToLot(lot, form)
                }
                return
            }

            lot.name = form.name
            lot.active = form.active

            if (lot.borders?.[0]) {
                const border = lot.borders[0]
                border.valid_from = form.border.valid_from ? form.border.valid_from.toISOString() : null
                border.valid_to = form.border.valid_to ? form.border.valid_to.toISOString() : null
            }

            if (!Array.isArray(lot.paths) || !Array.isArray(form.paths)) return

            form.paths.forEach((pathForm, index) => {
                const path = pathForm.id != null
                    ? lot.paths.find(p => p.id === pathForm.id)
                    : lot.paths[index]
                if (!path) return

                path.valid_from = pathForm.valid_from ? pathForm.valid_from.toISOString() : null
                path.valid_to = pathForm.valid_to ? pathForm.valid_to.toISOString() : null
            })
        },
        loadParkingLotEditForm() {
            const lot = this.editingParkingLotId != null
                ? this.existingParkingLots.find(p => p.id === this.editingParkingLotId)
                : null
            const newLot = this.selectedNewParkingLotId != null
                ? this.getNewParkingLotById(this.selectedNewParkingLotId)
                : null

            if (lot) {
                this.parkingLotForm = this.parkingLotFormFromLot(lot)
                this.parkingLotFormSnapshot = this.isPlannedParkingSlot(lot)
                    ? this.normalizeNewLotFormForCompare(this.parkingLotForm)
                    : this.normalizeFormForCompare(this.parkingLotForm)
            } else if (newLot) {
                this.parkingLotForm = this.parkingLotFormFromNewLot(newLot)
                this.parkingLotFormSnapshot = this.normalizeNewLotFormForCompare(this.parkingLotForm)
            } else {
                this.parkingLotForm = this.defaultParkingLotForm()
                this.parkingLotFormSnapshot = null
            }
        },
        hasParkingLotEditChanges() {
            const editingLot = this.editingParkingLotId != null
                ? this.existingParkingLots.find(p => p.id === this.editingParkingLotId)
                : null
            const newLot = this.selectedNewParkingLotId != null
                ? this.getNewParkingLotById(this.selectedNewParkingLotId)
                : null

            if (editingLot && this.lotDiffersFromBackup(editingLot, this.editingParkingLotBackup)) {
                return true
            }
            if (newLot && this.lotDiffersFromBackup(newLot, this.newParkingLotBackup)) {
                return true
            }

            if (!this.parkingLotForm || !this.parkingLotFormSnapshot) return false

            const useSimpleForm = this.selectedNewParkingLotId != null
                || this.isPlannedParkingSlot(editingLot)
            const normalize = useSimpleForm
                ? this.normalizeNewLotFormForCompare
                : this.normalizeFormForCompare

            return JSON.stringify(normalize(this.parkingLotForm))
                !== JSON.stringify(this.parkingLotFormSnapshot)
        },
        applyParkingLotEditForm() {
            if (!this.hasParkingLotEditChanges()) return false

            const newPark = useNewPlannedParkingLotStore()
            const form = this.parkingLotForm
            let lotToRedrawPath = null

            if (this.editingParkingLotId != null) {
                const lotIdx = this.existingParkingLots.findIndex(p => p.id === this.editingParkingLotId)
                if (lotIdx >= 0) {
                    const lot = this.existingParkingLots[lotIdx]
                    if (this.isPlannedParkingSlot(lot) && lot.terraDrawFeatureId) {
                        newPark.syncLotBorderFromTerraDraw(lot)
                    }
                    this.applyFormToLot(lot, form)
                    lot.isModified = true
                    if (this.isPlannedParkingSlot(lot) && !this.skipPlannedPathRegen) {
                        lotToRedrawPath = lot
                    }
                    this.parkingLotFormSnapshot = this.isPlannedParkingSlot(lot)
                        ? this.normalizeNewLotFormForCompare(form)
                        : this.normalizeFormForCompare(form)
                }
            } else if (this.selectedNewParkingLotId != null) {
                const newLot = this.getNewParkingLotById(this.selectedNewParkingLotId)
                if (newLot) {
                    this.applyPlannedPathFormFieldsToLot(newLot, form)
                    lotToRedrawPath = newLot
                    this.parkingLotFormSnapshot = this.normalizeNewLotFormForCompare(form)
                }
            }

            if (lotToRedrawPath) {
                newPark.applyPathToLot(lotToRedrawPath)
            }

            return true
        },
        openParkingLotEdit(lot) {
            const newPark = useNewPlannedParkingLotStore()

            if (this.selectedNewParkingLotId != null) {
                this.commitNewParkingLotEdit(this.selectedNewParkingLotId)
                this.resetNewLotEditSession()
            }

            if (this.editingParkingLotId != null && this.editingParkingLotId !== lot.id) {
                const prev = this.existingParkingLots.find(p => p.id === this.editingParkingLotId)
                if (prev) this.discardParkingLotEdit(prev)
            }

            newPark.endAllExistingPlannedTerraEdits(lot.id)
            this.selectedNewParkingLotId = null
            this.editingParkingLotId = lot.id
            this.skipPlannedPathRegen = this.isPlannedParkingSlot(lot)
            this.ensureParkingLotMapClickListener()
            this.selectParkingLot(lot.id)
            if (this.hasBorder(lot)) {
                this.centerMapOnParkingLot(lot)
            }
            if (this.isPlannedParkingSlot(lot)) {
                newPark.beginExistingLotEdit(lot)
            }
            this.editingParkingLotBackup = this.cloneParkingLotState(lot)
            this.loadParkingLotEditForm()
        },
        closeParkingLotEdit({ keepChanges = false } = {}) {
            const newPark = useNewPlannedParkingLotStore()

            if (this.editingParkingLotId != null) {
                const lot = this.existingParkingLots.find(p => p.id === this.editingParkingLotId)
                if (lot) {
                    if (keepChanges) {
                        if (this.isPlannedParkingSlot(lot)) {
                            newPark.endExistingLotEdit(lot)
                        } else {
                            this.drawParkingLotOnMap(lot)
                        }
                    } else {
                        this.discardParkingLotEdit(lot)
                    }
                }
            }
            this.resetExistingEditSession()
        },
        buildModifiedGeometryForSave(item, parkingLotId = null) {
            if (!item) return null

            const payload = {
                valid_from: item.valid_from ?? null,
                valid_to: item.valid_to ?? null,
                geometry: item.geometry ? JSON.parse(JSON.stringify(item.geometry)) : null,
                generator_version: item.generator_version ?? null,
            }

            if (
                item.id != null
                && (parkingLotId == null || !this.isClientBorderId(item.id, parkingLotId))
            ) {
                payload.id = item.id
            }

            return payload
        },
        buildModifiedParkingLotForSave(lot) {
            const fcs = useFieldsConnectionStore()
            const organization_id = fcs.selectedCustomer?.id

            if (!organization_id) return null

            const pl = lot || {}
            return {
                id: pl.id,
                name: pl.name,
                active: pl.active ?? null,
                organization_id,
                borders: (Array.isArray(pl.borders) ? pl.borders : [])
                    .map(border => this.buildModifiedGeometryForSave(border, pl.id))
                    .filter(Boolean),
                paths: (Array.isArray(pl.paths) ? pl.paths : [])
                    .map(path => this.buildModifiedGeometryForSave(path))
                    .filter(Boolean),
            }
        },
        buildPlannedParkingSlotForSave(lot) {
            const geometry = lot?.feature?.geometry ?? lot?.border
            if (geometry?.type !== 'Polygon') return null

            const payload = {
                name: lot.name ?? '',
                active: lot.active ?? null,
                border: JSON.parse(JSON.stringify(geometry)),
                generator_version: lot.generator_version ?? 1,
                survey_id: lot.survey_id ?? null,
            }

            const pathGeometry = lot.path ?? lot.feature?.path ?? null
            if (pathGeometry?.coordinates) {
                payload.path = JSON.parse(JSON.stringify(pathGeometry))
            }

            return payload
        },
        buildModifiedPlannedSlotForSave(lot) {
            const border = lot?.border ?? lot?.feature?.geometry
            if (border?.type !== 'Polygon') return null

            const payload = {
                id: lot.id,
                name: lot.name ?? '',
                active: lot.active ?? null,
                border: JSON.parse(JSON.stringify(border)),
                generator_version: lot.generator_version ?? 1,
                survey_id: lot.survey_id ?? null,
            }

            if (lot.path?.coordinates) {
                payload.path = JSON.parse(JSON.stringify(lot.path))
            }

            return payload
        },
        /** Payload for save. */
        getParkingLotsSavePayload() {
            const planned_parking_slots = this.newParkingLots
                .map(lot => this.buildPlannedParkingSlotForSave(lot))
                .filter(Boolean)

            const modified_parking_lots = this.existingParkingLots
                .filter(lot => lot.isModified && !this.isPlannedParkingSlot(lot))
                .map(lot => this.buildModifiedParkingLotForSave(lot))

            const modified_planned_parking_slots = this.existingParkingLots
                .filter(lot => lot.isModified && this.isPlannedParkingSlot(lot))
                .map(lot => this.buildModifiedPlannedSlotForSave(lot))
                .filter(Boolean)

            return {
                planned_parking_slots,
                modified_parking_lots,
                modified_planned_parking_slots,
            }
        },
        hasInvalidPlannedParkingAreasToSave() {
            const { planned_parking_slots, modified_planned_parking_slots } = this.getParkingLotsSavePayload()
            return [...planned_parking_slots, ...modified_planned_parking_slots]
                .some(slot => (slot.path?.coordinates?.length ?? 0) < 2)
        },
        hasParkingLotsToSave() {
            if (this.hasInvalidPlannedParkingAreasToSave()) return false

            const {
                planned_parking_slots,
                modified_parking_lots,
                modified_planned_parking_slots,
            } = this.getParkingLotsSavePayload()

            return planned_parking_slots.length > 0
                || modified_parking_lots.length > 0
                || modified_planned_parking_slots.length > 0
        },
        normalizeParkingLotsResponse(response) {
            const data = response?.data
            if (Array.isArray(data)) return data
            return data?.parking_lots ?? []
        },
        normalizePlannedSlotsResponse(response) {
            const data = response?.data
            if (Array.isArray(data)) return data
            return data?.planned_parking_slots ?? []
        },
        async loadParkingLots(farmId) {
            if (!farmId) return

            try {
                this.isLoading = true
                this.errorMessage = null
                this.clearParkingLots()

                const [parkingLotsRes, plannedSlotsRes] = await Promise.all([
                    getParkingLots(farmId),
                    getPlannedParkingSlots(farmId),
                ])
                const parkingLots = this.normalizeParkingLotsResponse(parkingLotsRes).map(pl =>
                    this.initExistingParkingLot(pl, false))
                const plannedSlots = this.normalizePlannedSlotsResponse(plannedSlotsRes).map(slot =>
                    this.initExistingParkingLot(slot, true))
                const lots = [...parkingLots, ...plannedSlots]

                this.existingParkingLots = this.sortExistingParkingLotsForDisplay(lots)

                this.drawParkingLots(lots)
            } catch (error) {
                console.error('Error loading parking lots:', error)
                this.errorMessage = error.message
            } finally {
                this.isLoading = false
            }
        },
        getParkingLotPathIds(parkingLot) {
            const paths = Array.isArray(parkingLot?.paths) ? parkingLot.paths : []
            return paths
                .map(pathItem => pathItem?.id)
                .filter(id => id != null)
        },
        drawParkingLots(parkingLots) {
            if (!Array.isArray(parkingLots) || parkingLots.length === 0) return
            parkingLots.forEach(lot => this.drawParkingLotOnMap(lot))
            this.ensureParkingLotMapClickListener()
        },
        /** Planned parking area (blue P) vs legacy parking position (green pin). */
        getParkingLotIconUrl(lot) {
            return this.isPlannedParkingSlot(lot) ? parkingAreaIcon : parkingPositionIcon
        },
        refreshParkingSignIcon(lot) {
            const geometry = this.isPlannedParkingSlot(lot)
                ? lot?.border
                : this.getBorder(lot)?.geometry
            const ring = geometry?.coordinates?.[0]
            if (!ring || lot?.id == null) return

            const overlays = useMapSvgOverlaysStore()
            overlays.removeByParkingLotId(PARKING_LOTS_OVERLAY_GROUP, lot.id)
            overlays.addIcon({
                group: PARKING_LOTS_OVERLAY_GROUP,
                ring,
                iconUrl: this.getParkingLotIconUrl(lot),
                sizePx: PARKING_SIGN_SIZE_PX,
                options: {
                    parkingLotId: lot.id,
                    onClick: (position) => this.openParkingLotFromMap(lot.id, position),
                },
            })
        },
        refreshAllParkingSignIcons() {
            const overlays = useMapSvgOverlaysStore()
            overlays.removeUntaggedInGroup(PARKING_LOTS_OVERLAY_GROUP)

            this.existingParkingLots.forEach(lot => {
                if (lot.terraDrawFeatureId) return
                this.refreshParkingSignIcon(lot)
            })
        },
        drawParkingLotOnMap(lot) {
            if (!lot) return

            this.removeParkingLotMapOverlays({
                parkingLotId: lot.id,
                borderId: this.getBorderId(lot),
                pathIds: this.getPathIds(lot),
            })

            if (this.isPlannedParkingSlot(lot)) {
                const border = this.getBorder(lot)
                if (border) this.drawBorder(border, lot)
                const pathGeometry = this.getPathGeometry(lot)
                if (pathGeometry) {
                    this.drawPath({ id: plannedPathId(lot.id), geometry: pathGeometry }, lot)
                }
                this.refreshParkingSignIcon(lot)
                if (this.selectedParkingLotId === lot.id) {
                    this.selectParkingLot(lot.id)
                }
                return
            }

            const borders = lot?.borders || []
            const paths = lot?.paths || []
            borders.forEach(border => this.drawBorder(border, lot))
            paths.forEach(pathItem => this.drawPath(pathItem, lot))
            this.refreshParkingSignIcon(lot)

            if (this.selectedParkingLotId === lot.id) {
                this.selectParkingLot(lot.id)
            }
        },
        attachParkingLotMapStyles(mapObject, type, { strokeColor = PATH_STROKE_COLOR } = {}) {
            if (type === 'polygon') {
                mapObject.normalStyle = {
                    fillColor: PARKING_LOT_FILL_COLOR,
                    fillOpacity: PARKING_LOT_FILL_OPACITY,
                    strokeColor: PARKING_LOT_BORDER_COLOR,
                    strokeWeight: PARKING_LOT_BORDER_STROKE_WEIGHT,
                }
                mapObject.highlightStyle = {
                    fillColor: PARKING_LOT_FILL_COLOR,
                    fillOpacity: PARKING_LOT_FILL_OPACITY,
                    strokeColor: PARKING_LOT_BORDER_COLOR,
                    strokeWeight: PARKING_LOT_SELECTED_STROKE_WEIGHT,
                }
            } else {
                mapObject.normalStyle = {
                    strokeColor,
                    strokeWeight: PATH_STROKE_WEIGHT,
                }
                mapObject.highlightStyle = {
                    strokeColor,
                    strokeWeight: PARKING_LOT_SELECTED_STROKE_WEIGHT,
                }
            }
        },
        selectParkingLot(parkingLotId) {
            if (!parkingLotId) return

            this.clearParkingLotHighlight()
            this.selectedParkingLotId = parkingLotId

            this.parkingLots.forEach(mapObject => {
                const raw = toRaw(mapObject)
                if (raw.parkingLotId === parkingLotId && raw.highlightStyle) {
                    raw.setOptions(raw.highlightStyle)
                }
            })
        },
        clearParkingLotHighlight() {
            const parkingLotId = this.selectedParkingLotId
            if (!parkingLotId) return

            this.parkingLots.forEach(mapObject => {
                const raw = toRaw(mapObject)
                if (raw.parkingLotId === parkingLotId && raw.normalStyle) {
                    raw.setOptions(raw.normalStyle)
                }
            })
            this.selectedParkingLotId = null
        },
        clearParkingInfoWindowContent() {
            if (this.parkingInfoWindowEl) {
                render(null, this.parkingInfoWindowEl)
            }
        },
        closeAllParkingInfoWindows() {
            if (this.parkingInfoWindow) {
                this.parkingInfoWindow.close()
            }
            this.clearParkingInfoWindowContent()
        },
        openParkingLotFromMap(parkingLotId, position) {
            this.closeAllParkingInfoWindows()
            this.selectParkingLot(parkingLotId)
            document.getElementById(`parking-lot-${parkingLotId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })

            const lot = this.existingParkingLots.find(p => p.id === parkingLotId)
            if (!lot) return

            this.openParkingInfoWindow(lot, position)
        },
        openParkingInfoWindow(lot, position) {
            const ms = useMaps()
            if (!ms.map || !ms.google) return

            this.clearParkingInfoWindowContent()

            if (!this.parkingInfoWindowEl) {
                this.parkingInfoWindowEl = document.createElement('div')
            }

            const vnode = createVNode(ParkingLotInfoWindow, { lot })
            vnode.appContext = app._context
            render(vnode, this.parkingInfoWindowEl)

            if (!this.parkingInfoWindow) {
                this.parkingInfoWindow = new ms.google.maps.InfoWindow()
            }

            this.parkingInfoWindow.setContent(this.parkingInfoWindowEl)
            this.parkingInfoWindow.setPosition(position)
            this.parkingInfoWindow.open({ map: toRaw(ms.map) })
        },
        ensureParkingLotMapClickListener() {
            const ms = useMaps()

            if (this.mapClickListener || !ms.map || !ms.google) return

            this.mapClickListener = ms.google.maps.event.addListener(
                toRaw(ms.map),
                'click',
                () => {
                    this.clearParkingLotHighlight()
                    this.closeAllParkingInfoWindows()
                }
            )
        },
        showNewParkingLotsOnMapForMeasure() {
            const newPark = useNewPlannedParkingLotStore()
            newPark._ignoreTerraDrawChanges = true
            this._newLotIdsOnMapForMeasure = []

            this.newParkingLots.forEach((lot) => {
                const geometry = lot.feature?.geometry
                if (geometry?.type !== 'Polygon') return

                if (lot.terraDrawFeatureId) {
                    newPark.removeLotFromTerraDraw(lot)
                }

                this.drawBorder({ id: plannedBorderId(lot.id), geometry }, lot)

                if (lot.path?.coordinates?.length && !lot.pathPolylines?.length) {
                    newPark.drawPathPolylines(lot)
                }

                this._newLotIdsOnMapForMeasure.push(lot.id)
            })
        },
        restoreNewParkingLotsToTerraAfterMeasure() {
            const newPark = useNewPlannedParkingLotStore()

            if (!this._newLotIdsOnMapForMeasure.length) {
                newPark._ignoreTerraDrawChanges = false
                return
            }
            const selectedId = this.selectedNewParkingLotId
            const lotIds = [...this._newLotIdsOnMapForMeasure]
            this._newLotIdsOnMapForMeasure = []

            lotIds.forEach((id) => {
                const lot = this.getNewParkingLotById(id)
                if (!lot?.feature?.geometry) return

                this.removeParkingLotMapOverlays({
                    parkingLotId: lot.id,
                    borderId: plannedBorderId(lot.id),
                    pathIds: this.getPathIds(lot),
                })

                newPark.addLotToTerraDraw(lot)
                if (lot.path?.coordinates?.length) {
                    newPark.drawPathPolylines(lot)
                }
            })

            if (selectedId) {
                const lot = this.getNewParkingLotById(selectedId)
                if (lot?.terraDrawFeatureId) {
                    newPark.activateLotSelect(selectedId)
                }
            }

            newPark._ignoreTerraDrawChanges = false
        },
        setParkingMapOverlaysClickable(clickable) {
            this.parkingLots.forEach(mapObject => {
                const raw = toRaw(mapObject)
                if (typeof raw?.setOptions !== 'function') return

                if (clickable && raw.borderId != null) {
                    raw.setOptions({ clickable: true })
                } else {
                    raw.setOptions({ clickable: false })
                }
            })

            const lots = [...this.existingParkingLots, ...this.newParkingLots]
            lots.forEach(lot => {
                if (!Array.isArray(lot.pathPolylines)) return
                lot.pathPolylines.forEach(polyline => {
                    toRaw(polyline)?.setOptions?.({ clickable: false })
                })
            })
        },
        pauseMapClickListener() {
            const ms = useMaps()

            if (this.mapClickListener && ms.google?.maps?.event) {
                ms.google.maps.event.removeListener(this.mapClickListener)
                this.mapClickListener = null
                this._wasListeningBeforeMeasure = true
            }

            if (this.parkingLots.length > 0 || this.existingParkingLots.length > 0 || this.newParkingLots.length > 0) {
                this.closeAllParkingInfoWindows()
                this.clearParkingLotHighlight()
                if (this.newParkingLots.length > 0) {
                    this.showNewParkingLotsOnMapForMeasure()
                }
                this.setParkingMapOverlaysClickable(false)
                this._parkingMapInteractionPausedForMeasure = true
            }
        },
        resumeMapClickListener() {
            if (this._parkingMapInteractionPausedForMeasure) {
                this.restoreNewParkingLotsToTerraAfterMeasure()
                this._parkingMapInteractionPausedForMeasure = false
                this.setParkingMapOverlaysClickable(true)
            }

            if (this._wasListeningBeforeMeasure) {
                this._wasListeningBeforeMeasure = false
                this.ensureParkingLotMapClickListener()
            }
        },
        drawBorder(border, parkingLot) {
            if (!border?.id) return

            const geometry = border?.geometry
            if (!geometry?.coordinates) return

            if (geometry.type === 'Polygon') {
                this.createPolygon(geometry.coordinates, parkingLot, border)
            }
        },
        drawPath(pathItem, parkingLot) {
            const geometry = pathItem?.geometry
            if (geometry?.type !== 'MultiLineString' || !geometry.coordinates) return

            const useCenterColor = this.isPlannedParkingSlot(parkingLot)
            geometry.coordinates.forEach((coords, i) => {
                const color = useCenterColor && i === 0 ? CENTER_PATH_STROKE_COLOR : PATH_STROKE_COLOR
                this.createPolyline(coords, parkingLot, pathItem, color)
            })
        },
        createPolygon(polygonCoordinates, parkingLot, border) {
            const ms = useMaps()

            if (!ms.map || !ms.google) return

            const paths = polygonCoordinates.map(ring =>
                ring.map(([lng, lat]) => ({ lat, lng }))
            )

            const polygon = new ms.google.maps.Polygon({
                paths,
                fillColor: PARKING_LOT_FILL_COLOR,
                fillOpacity: PARKING_LOT_FILL_OPACITY,
                strokeColor: PARKING_LOT_BORDER_COLOR,
                strokeOpacity: 1,
                strokeWeight: PARKING_LOT_BORDER_STROKE_WEIGHT,
                zIndex: PARKING_LOT_Z_INDEX,
                clickable: true
            })
            polygon.setMap(ms.map)
            polygon.parkingLotId = parkingLot?.id
            polygon.borderId = border?.id
            polygon.pathIds = this.getParkingLotPathIds(parkingLot)
            this.attachParkingLotMapStyles(polygon, 'polygon')
            this.parkingLots.push(toRaw(polygon))

            polygon.addListener('click', (event) => {
                this.openParkingLotFromMap(parkingLot.id, event.latLng)
            })

        },
        createPolyline(lineCoordinates, parkingLot, pathItem, strokeColor = PATH_STROKE_COLOR) {
            const ms = useMaps()

            if (!ms.map || !ms.google) return

            const path = lineCoordinates.map(([lng, lat]) => ({ lat, lng }))

            const polyline = new ms.google.maps.Polyline({
                path,
                strokeColor,
                strokeOpacity: 1,
                strokeWeight: PATH_STROKE_WEIGHT,
                zIndex: PARKING_LOT_Z_INDEX
            })
            polyline.setMap(ms.map)
            polyline.parkingLotId = parkingLot?.id
            polyline.pathId = pathItem?.id
            this.attachParkingLotMapStyles(polyline, 'polyline', { strokeColor })
            this.parkingLots.push(toRaw(polyline))
        },
        removeParkingLotMapOverlays({ borderId, pathIds, parkingLotId } = {}) {
            const ms = useMaps()

            const pathIdSet = new Set(
                Array.isArray(pathIds) ? pathIds : (pathIds != null ? [pathIds] : [])
            )
            this.parkingLots = this.parkingLots.filter(mapObject => {
                const raw = toRaw(mapObject)
                const shouldRemove = (parkingLotId != null && raw.parkingLotId === parkingLotId)
                    || (borderId != null && raw.borderId === borderId)
                    || (pathIdSet.size > 0 && pathIdSet.has(raw.pathId))

                if (shouldRemove) {
                    if (raw && ms.google?.maps?.event) {
                        ms.google.maps.event.clearListeners(raw, 'click')
                    }
                    raw?.setMap?.(null)
                    return false
                }
                return true
            })

            if (parkingLotId != null) {
                useMapSvgOverlaysStore().removeByParkingLotId(
                    PARKING_LOTS_OVERLAY_GROUP,
                    parkingLotId,
                )
            }
        },
        clearParkingLots() {
            this.clearNewParkingLots()
            this.clearAllBorderEdits()
            this.closeParkingLotEdit()
            this.clearParkingLotHighlight()
            this.closeAllParkingInfoWindows()

            const ms = useMaps()
            if (this.mapClickListener && ms.google?.maps?.event) {
                ms.google.maps.event.removeListener(this.mapClickListener)
                this.mapClickListener = null
            }

            this.parkingLots.forEach(mapObject => {
                const raw = toRaw(mapObject)
                if (raw && ms.google?.maps?.event) {
                    ms.google.maps.event.clearListeners(raw, 'click')
                }
                if (raw && typeof raw.setMap === 'function') {
                    raw.setMap(null)
                }
            })
            this.parkingLots = []
            this.existingParkingLots = []

            const overlays = useMapSvgOverlaysStore()
            overlays.clearGroup(PARKING_LOTS_OVERLAY_GROUP)
        },
        async uploadParkingLotFile(file) {
            const maps = useMaps()

            const formData = new FormData()
            formData.append('file', file)
            formData.append('desired_type', 'polygon')
            
            try {
                const response = await postExtractGeometryFromShape(formData)
                
                // Handle not supported file format
                if (response.status === 422) {
                    return { success: false, error: 'Unsupported file format' }
                }

                if (!response) {
                    throw new Error('Failed to upload file!')
                }

                const features = response.cutout?.features || []

                if (features.length !== 1) {
                    return { success: false, error: 'invalid_file' }
                }

                const feature = features[0]
                if (feature.properties?.field_geom_type !== 'cutout' || !isRectangle(feature)) {
                    return { success: false, error: 'no_rectangle' }
                }

                const newLot = this.addNewParkingLotFromFeature(feature)

                const newPark = useNewPlannedParkingLotStore()
                if (maps.terraDraw?.enabled) {
                    maps.terraDraw.setMode('select')
                } else {
                    newPark.ensureTerraDrawActive()
                    maps.terraDraw?.setMode('select')
                }
                if (newLot) {
                    this.selectNewParkingLot(newLot.id)
                    this.centerMapOnParkingLot(newLot)
                }

                return { success: true }
            } catch (error) {
                console.error('Error uploading parking lot file:', error)
                return { success: false }
            }
        },
        async deleteParkingLot(farm_id, parking_lot_id) {
            if (!farm_id || !parking_lot_id) return false

            const lot = this.existingParkingLots.find(p => p.id === parking_lot_id)
            if (!lot) return false

            if (this.isPlannedParkingSlot(lot)) {
                return this.deletePlannedParkingSlot(farm_id, parking_lot_id)
            }

            try {
                const success = await deleteParkingLot(farm_id, parking_lot_id)
                if (!success) return false

                this.closeAllParkingInfoWindows()
                this.clearBorderEdit(parking_lot_id)
                this.removeParkingLotMapOverlays({
                    parkingLotId: parking_lot_id,
                    borderId: this.getBorderId(lot),
                    pathIds: this.getPathIds(lot),
                })
                this.existingParkingLots = this.existingParkingLots.filter(
                    p => p.id !== parking_lot_id
                )
                if (this.selectedParkingLotId === parking_lot_id) {
                    this.selectedParkingLotId = null
                }
                if (this.editingParkingLotId === parking_lot_id) {
                    this.closeParkingLotEdit()
                }
                return true
            } catch (error) {
                console.error('Error deleting parking lot:', error)
                return false
            }
        },
        async deletePlannedParkingSlot(farm_id, slot_id) {
            if (!farm_id || !slot_id) return false

            const lot = this.existingParkingLots.find(p => p.id === slot_id)
            if (!lot || !this.isPlannedParkingSlot(lot)) return false

            const newPark = useNewPlannedParkingLotStore()
            if (lot.terraDrawFeatureId) {
                newPark.removeLotFromTerraDraw(lot)
            }
            newPark.clearLotPathPolylines(lot)

            try {
                const success = await deletePlannedParkingSlotApi(farm_id, slot_id)
                if (!success) return false

                this.closeAllParkingInfoWindows()
                this.removeParkingLotMapOverlays({
                    parkingLotId: slot_id,
                    borderId: this.getBorderId(lot),
                    pathIds: this.getPathIds(lot),
                })
                this.existingParkingLots = this.existingParkingLots.filter(p => p.id !== slot_id)
                if (this.selectedParkingLotId === slot_id) {
                    this.selectedParkingLotId = null
                }
                if (this.editingParkingLotId === slot_id) {
                    this.closeParkingLotEdit()
                }
                return true
            } catch (error) {
                console.error('Error deleting planned parking slot:', error)
                return false
            }
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useParkingLotsStore, import.meta.hot))
}
